import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Send, LogOut, MessagesSquare, Car, User } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";

export default function Chat() {
  const [params, setParams] = useSearchParams();
  const [convs, setConvs] = useState(null);
  const [active, setActive] = useState(params.get("open"));
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const bottomRef = useRef(null);
  const lastCount = useRef(0);

  const loadConvs = useCallback(
    () => api.get("/chat/conversations").then(({ data }) => setConvs(data)).catch(() => setConvs([])),
    []
  );
  useEffect(() => { loadConvs(); }, [loadConvs]);

  const loadThread = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/chat/${id}/messages`);
      setThread(data);
    } catch { setThread(false); }
  }, []);

  useEffect(() => {
    if (!active) { setThread(null); return; }
    loadThread(active);
    const t = setInterval(() => loadThread(active), 4000);
    return () => clearInterval(t);
  }, [active, loadThread]);

  useEffect(() => {
    const n = thread?.messages?.length || 0;
    if (n !== lastCount.current) {
      lastCount.current = n;
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [thread]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await api.post(`/chat/${active}/messages`, { content: text });
      setText("");
      loadThread(active);
      loadConvs();
    } catch (err) { toast.error(apiError(err)); }
  };

  const leave = async () => {
    try {
      await api.post(`/chat/${active}/leave`);
      setActive(null); setParams({}); loadConvs();
      toast.success("You left the chat");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="chat-page">
      <h1 className="font-display font-black text-3xl">Chat</h1>
      <p className="text-xs text-muted-foreground -mt-2">Chat with confirmed ride members and buddies — no phone numbers shared.</p>
      <div className="grid md:grid-cols-[300px_1fr] gap-4">
        <div className="t-card overflow-hidden divide-y divide-black/5 h-fit max-h-[70vh] overflow-y-auto">
          {convs === null ? (
            <p className="p-4 text-sm text-muted-foreground animate-pulse">Loading…</p>
          ) : convs.length ? (
            convs.map((c) => (
              <button key={c.id} data-testid={`conversation-${c.id}`}
                onClick={() => { setActive(c.id); setParams({ open: c.id }); }}
                className={`flex items-start gap-3 w-full text-left p-4 transition-colors duration-150 ${active === c.id ? "bg-primary/[0.06]" : "hover:bg-black/[0.02]"}`}>
                <div className="w-9 h-9 rounded-full bg-secondary/30 flex items-center justify-center shrink-0">
                  {c.type === "ride" ? <Car className="w-4 h-4" /> : <User className="w-4 h-4" />}
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-sm truncate">{c.title}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {c.last_message ? c.last_message.content : "No messages yet"}
                  </p>
                </div>
              </button>
            ))
          ) : (
            <div className="p-6 text-center text-sm text-muted-foreground" data-testid="no-conversations">
              No chats yet. Open one from a confirmed ride or a buddy's row.
            </div>
          )}
        </div>

        <div className="t-card flex flex-col h-[70vh]">
          {!active || !thread ? (
            <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
              <MessagesSquare className="w-10 h-10" />
              <p className="text-sm mt-3">{thread === false ? "Chat not found." : "Select a conversation"}</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between px-5 py-3 border-b border-black/5">
                <p className="font-bold text-sm truncate" data-testid="thread-title">{thread.conversation.title}</p>
                <button data-testid="leave-chat-btn" onClick={leave}
                  className="flex items-center gap-1 text-xs font-bold text-muted-foreground hover:text-destructive transition-colors duration-200">
                  <LogOut className="w-3.5 h-3.5" /> Leave
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="messages-list">
                {thread.messages.length === 0 && (
                  <p className="text-center text-sm text-muted-foreground" data-testid="no-messages">Say hi 👋 — plan your pickup, split the fare, coordinate timing.</p>
                )}
                {thread.messages.map((m) => (
                  <div key={m.id} className={`flex ${m.is_mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
                    <div className={`max-w-[75%] rounded-3xl px-4 py-2.5 ${m.is_mine ? "bg-primary text-white rounded-br-lg" : "bg-black/5 rounded-bl-lg"}`}>
                      {!m.is_mine && <p className="text-[10px] font-bold uppercase tracking-wider opacity-70">{m.sender_name}</p>}
                      <p className="text-sm whitespace-pre-wrap break-words">{m.content}</p>
                      <p className={`text-[10px] mt-0.5 ${m.is_mine ? "text-white/70" : "text-muted-foreground"}`}>
                        {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              <form onSubmit={send} className="flex gap-2 p-4 border-t border-black/5">
                <Input data-testid="chat-input" placeholder="Type a message…" value={text}
                  onChange={(e) => setText(e.target.value)} className="rounded-full" />
                <button data-testid="chat-send-btn" type="submit"
                  className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center shrink-0 hover:-translate-y-0.5 transition-transform duration-200">
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
